import { courseRuntimeUrl, capturedCourseRuntimeUrl, CapturedCourseContext } from './courseRuntimeContext';
import api from '../httpClient';
import type { ResponsePayload } from '../../types';

const writePayload = <T extends Partial<ResponsePayload>>(data: T): T => {
  if (!data.metadata || !Object.prototype.hasOwnProperty.call(data.metadata, 'ai_generation')) return data;
  const { ai_generation, ...metadata } = data.metadata;
  return { ...data, metadata };
};

export const responses = {
  list: (params?: Record<string, unknown>) => api.get(courseRuntimeUrl('/api/responses/my/'), { params }),
  create: (data: ResponsePayload, context?: CapturedCourseContext) => api.post(context ? capturedCourseRuntimeUrl('/api/responses/', context) : courseRuntimeUrl('/api/responses/'), writePayload(data)),
  update: (id: number, data: Partial<ResponsePayload>, context?: CapturedCourseContext) =>
    api.put(context ? capturedCourseRuntimeUrl(`/api/responses/${id}/`, context) : courseRuntimeUrl(`/api/responses/${id}/`), writePayload(data)),
  createWithAudio: (formData: FormData) =>
    api
      .post(courseRuntimeUrl('/api/responses/'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => response.data),
  getHistory: () => api.get(courseRuntimeUrl('/api/response-history/')),
  getAudioQuestions: () => api.get(courseRuntimeUrl('/api/audio-questions/')),
  submitResponseWithAudio: (
    questionId: number,
    audioBlob: Blob,
    textValue?: string,
    extras: Partial<ResponsePayload> = {},
  ) => {
    extras = writePayload(extras);
    const formData = new FormData();
    formData.append('audio', audioBlob);
    formData.append(
      'data',
      JSON.stringify({
        question: questionId,
        input_mode: extras.input_mode ?? 'standard',
        text_value: textValue,
        flow_item: extras.flow_item,
        flow_run: extras.flow_run,
        generation_id: extras.generation_id,
        rating_value: extras.rating_value,
        boolean_value: extras.boolean_value,
        selected_options: extras.selected_options,
        revealed_options: extras.revealed_options,
        custom_other_value: extras.custom_other_value,
        text_list: extras.text_list,
        metadata: extras.metadata,
      }),
    );

    return api
      .post(courseRuntimeUrl('/api/responses/'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        if (!response.data) {
          throw new Error('No response data received from server');
        }

        if ((response.data as { question?: number }).question === questionId) {
          return { data: response.data };
        }

        return {
          data: {
            question: questionId,
            input_mode: extras.input_mode ?? 'standard',
            text_value: textValue,
            audio_url: (response.data as { audio_url?: string }).audio_url,
            flow_item: extras.flow_item,
            flow_run: extras.flow_run,
          },
        };
      });
  },
  getRemainingQuestionsCount: () => api.get(courseRuntimeUrl('/api/remaining-questions-count/')),
  getRadarChartData: (definitionKey: string) =>
    api.get(courseRuntimeUrl(`/api/radar-chart-data/${encodeURIComponent(definitionKey)}/`)),
  transcribeAudio: (audioBlob: Blob): Promise<{ transcription: string }> => {
    const formData = new FormData();
    const fileExtension = audioBlob.type === 'audio/wav' ? 'wav' : 'webm';
    formData.append('audio', audioBlob, `recording.${fileExtension}`);

    return api
      .post(courseRuntimeUrl('/api/transcribe-audio/'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        if (!response.data || typeof response.data.transcription !== 'string') {
          throw new Error('Invalid transcription response format');
        }
        return response.data;
      });
  },
};
